<script setup>
    import { ref, watchEffect } from 'vue';
    import { getFromAPI, buildPageFooter, isButtonDisabled } from '../../functionCollection.js';
    import axios from 'axios';

    const props = defineProps(['fetchUrl', 'userNumber', 'maxKeys', 'lockUrl', 'showAuthMediaUrl', 'isAdmin']);
    const mobilityKeyInfo = ref(null);
    const mobilityKeyToLock = ref(null);
    const columnsToFetch = "id,identifier,can_use_bicycle,can_use_charge,locked"
    const reasonText = ref('');
    const submitResult = ref(null);

    // needed for the page footer
    const currentPage = ref(1);
    const maxPageNumber = ref(1);
    const footerPageNumbers = ref(new Array());

    watchEffect(async () => {
        const url = getFullUrl(props.fetchUrl);
        mobilityKeyInfo.value = await getFromAPI(url);
        maxPageNumber.value = mobilityKeyInfo.value.meta.last_page;
        mobilityKeyInfo.value = mobilityKeyInfo.value.data;
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    });

    // complete the URL. the values in the last concat specify which page we want,
    // as well as which columns from Teilneher we need, and how many entries we want per page.
    function getFullUrl(url) {
        return url.concat('/').concat(props.userNumber).concat(`?page=${currentPage.value}&columns=${columnsToFetch}&column=id&order=asc&per_page=10`);
    }

    // calls new Teilnehmer info whenever a page change is requested (i.e. a page button is pressed in the footer)
    async function getNewPage(newPage) {
        currentPage.value = newPage;
        if (currentPage.value < 1) {
            currentPage.value = 1;
        }
        mobilityKeyInfo.value = await getFromAPI(getFullUrl(props.fetchUrl));
        footerPageNumbers.value = buildPageFooter(currentPage.value, maxPageNumber.value);
    }

    // submits a mobility key to lock.
    async function submit() {
        submitResult.value = null;
        if (!mobilityKeyToLock.value) {
            submitResult.value = 0; //it's not an HTTP return code, but still shows the error message.
            return;
        }
        let lockUrl = props.lockUrl.replace(':id', mobilityKeyToLock.value.id);
        // the saved result will show the corresponding error message.
        submitResult.value = await axios({
            method: 'put',
            url: lockUrl,
            data: {
                id: mobilityKeyToLock.value.id,
                Reason: reasonText.value,
            }
        });
    }

</script>
<template>
    <div class="well">
        <div v-if="!mobilityKeyInfo">
            <div class="alert alert-custom" role="alert">
                <i class="fas fa-lg fa-comment fa-4x"></i>
                <span>Es sind keine Mobility-Keys hinterlegt.</span>
            </div>
        </div>
        <div class="main-table" v-if="mobilityKeyInfo">
            <div class="inner-table">
                <table class="table">
                    <thead>
                        <tr>
                            <th class="table-head">ID</th>
                            <th class="table-head">Mobility-Key</th>
                            <th class="table-head">Radparken</th>
                            <th class="table-head">Ladeinfrastruktur</th>
                            <th class="table-head">Gesperrt</th>
                            <th class="table-head">(Ent)Sperren</th>
                        </tr>
                    </thead>


                    <tbody>
                        <tr class="m-datatable__row" v-for="mobilityKey of mobilityKeyInfo" :key="mobilityKey.counter">
                            <td>{{ mobilityKey.id }}</td>
                            <td>{{ mobilityKey.identifier }}</td>
                            <td>{{ mobilityKey.can_use_bicycle }}</td>
                            <td>{{ mobilityKey.can_use_charge }}</td>
                            <td>{{ mobilityKey.locked }}</td>
                            <div class="buttons">
                                <div v-if="props.isAdmin === 'true'">
                                    <button
                                        type="button"
                                        class="btn btn-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#modalMobilityKeyLock"
                                        @click="mobilityKeyToLock = mobilityKey"
                                    >
                                        {{ mobilityKey.locked === 'Ja' ? "Entsperren" : "Sperren" }}
                                    </button>
                                </div>
                            </div>
                        </tr>
                    </tbody>

                </table>
            </div>
        </div>

        <div class="table-footer middle" v-if="mobilityKeyInfo && footerPageNumbers.length > 1">
            <ul class="pagination">
                <li class="page-item">
                    <button class="page-link" :disabled="isButtonDisabled('start', currentPage, maxPageNumber)" @click="getNewPage(currentPage - 1)">
                        <i class="fas fa-lg fa-caret-left"></i>
                    </button>
                </li>
                <li v-for="page in footerPageNumbers" :key="page.id" :class="{ 'active' : page == currentPage }" >
                    <button class="page-link" @click="getNewPage(page)" :disabled="isButtonDisabled(page, currentPage, maxPageNumber)">{{ page }}</button>
                </li>
                <li class="page-item">
                    <button class="page-link" @click="getNewPage(currentPage + 1)" :disabled="isButtonDisabled('end', currentPage, maxPageNumber)">
                        <i class="fas fa-lg fa-caret-right"></i>
                    </button>
                </li>
            </ul>
        </div>
    </div>

    <div class="modal fade" id="modalMobilityKeyLock" tabindex="-1">
        <div class="modal-dialog">
            <div class="modal-content" v-if="mobilityKeyToLock">
                <div class="modal-header">
                    <h4 class="modal-title">Mobility-Key (ent)sperren</h4>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Schließen"></button>
                </div>
                <form>
                    <div class="modal-body">
                        <label for="reason">Grund</label>
                        <input v-model="reasonText" class="form-control" type="text" />
                    </div>
                    <button type="button" class="btn btn-primary" @click="submit">
                        {{ (mobilityKeyToLock.locked === "Nein") ? 'Mobility-Key sperren' : 'Mobility-Key entsperren' }}
                    </button>
                </form>
                <div class="alert alert-success" role="alert" v-if="submitResult && submitResult.status === 200">
                    Mobility-Key erfolgreich {{(mobilityKeyToLock.locked === "Nein") ? 'gesperrt' : 'entsperrt'}}!
                </div>
                <div class="alert alert-danger" role="alert" v-if="submitResult && submitResult.status !== 200">
                    Mobility-Key konnte nicht ge-/entsperrt werden.
                </div>
            </div>
            <!-- TODO Dismiss refreshes? -->
        </div>
    </div>
</template>
